import { useCallback, useEffect, useRef } from "react";

/**
 * 一个专注于启动和销毁的长轮询Hook
 * @param {Function} asyncTask 异步任务函数，需返回Promise
 * @param {number} interval 轮询间隔时间（毫秒）
 * @returns {Object} 包含启动和停止轮询方法的对象
 */
function useLongPolling(asyncTask: () => void, interval = 5000) {
  // 使用ref存储轮询状态和定时器ID，避免重复渲染
  const pollingActive = useRef(false);
  const timeoutId = useRef<NodeJS.Timeout | null>(null);

  // 停止轮询：清理定时器，重置状态
  const stopPolling = useCallback(() => {
    pollingActive.current = false;
    if (timeoutId.current) {
      clearTimeout(timeoutId.current);
      timeoutId.current = null;
    }
  }, []);

  // 执行单次轮询任务，并根据条件决定是否继续
  const executeTask = useCallback(async () => {
    if (!pollingActive.current) return;

    try {
      await asyncTask();
      // 任务成功后，安排下一次轮询
      if (pollingActive.current) {
        timeoutId.current = setTimeout(executeTask, interval);
      }
    } catch (error) {
      console.error("轮询任务执行失败:", error);
      // 即使任务失败，也继续轮询（可根据需要调整）
      if (pollingActive.current) {
        timeoutId.current = setTimeout(executeTask, interval);
      }
    }
  }, [asyncTask, interval]);

  // 启动轮询：如果未在运行，则开始执行
  const startPolling = useCallback(() => {
    if (pollingActive.current) {
      return;
    }
    pollingActive.current = true;
    executeTask(); // 立即开始第一次执行
  }, [executeTask]);

  // 组件卸载时自动停止轮询
  useEffect(() => {
    startPolling();
    return () => {
      stopPolling();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stopPolling]);
}

export default useLongPolling;
